import { useState } from "react";
import { ArrowLeftIcon, PlusIcon, EyeIcon, SaveIcon, SendIcon, CheckCircleIcon } from "lucide-react";
import { AcademicianButton } from "@/components/academician/academician-button";
import { QuestionEditor, Question, QuestionType } from "@/components/academician/question-editor";
import { QuizPreview } from "@/components/academician/quiz-preview";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { apiPost, apiPatch } from "@/lib/api";

interface QuizEditorPageProps {
  quizId?: string;
  initialData?: {
    title: string;
    description: string;
    passingScore: number;
    questions: Question[];
    status: "published" | "draft";
  };
  saveUrl?: string;
  backUrl?: string;
  uploadImageUrl?: string;
  onSave?: (data: Record<string, unknown>) => void;
  className?: string;
}

export function QuizEditorPage({
  quizId,
  initialData,
  saveUrl,
  backUrl = "/admin/quizzes",
  uploadImageUrl,
  onSave,
  className = "",
}: QuizEditorPageProps) {
  const [title, setTitle] = useState(initialData?.title || "");
  const [description, setDescription] = useState(
    initialData?.description || ""
  );
  const [passingScore, setPassingScore] = useState(
    initialData?.passingScore || 80
  );
  const [questions, setQuestions] = useState<Question[]>(
    initialData?.questions || []
  );
  const [status, setStatus] = useState<"draft" | "published">(
    initialData?.status === "published" ? "published" : "draft"
  );
  const [showPreview, setShowPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  const createNewQuestion = (type: QuestionType = "multiple-choice"): Question => {
    const timestamp = Date.now();
    const randomSuffix = Math.random().toString(36).substring(2, 9);

    if (type === "true-false") {
      return {
        id: `q-${timestamp}-${randomSuffix}`,
        text: "",
        type: "true-false",
        options: [
          { id: `opt-${timestamp}-${randomSuffix}-t`, text: "True", isCorrect: true, explanation: "" },
          { id: `opt-${timestamp}-${randomSuffix}-f`, text: "False", isCorrect: false, explanation: "" },
        ],
        correctAnswers: [],
      };
    }

    if (type === "fill-in-the-blank") {
      return {
        id: `q-${timestamp}-${randomSuffix}`,
        text: "",
        type: "fill-in-the-blank",
        options: [],
        correctAnswers: [""],
      };
    }

    return {
      id: `q-${timestamp}-${randomSuffix}`,
      text: "",
      type: "multiple-choice",
      options: [
        { id: `opt-${timestamp}-${randomSuffix}-a`, text: "", isCorrect: false, explanation: "" },
        { id: `opt-${timestamp}-${randomSuffix}-b`, text: "", isCorrect: false, explanation: "" },
        { id: `opt-${timestamp}-${randomSuffix}-c`, text: "", isCorrect: false, explanation: "" },
        { id: `opt-${timestamp}-${randomSuffix}-d`, text: "", isCorrect: false, explanation: "" },
      ],
      correctAnswers: [],
    };
  };

  const handleAddQuestion = () => {
    setQuestions([...questions, createNewQuestion()]);
  };

  const handleUpdateQuestion = (index: number, updatedQuestion: Question) => {
    const newQuestions = [...questions];
    newQuestions[index] = updatedQuestion;
    setQuestions(newQuestions);
  };

  const handleDeleteQuestion = (index: number) => {
    if (confirm("Are you sure you want to delete this question?")) {
      setQuestions(questions.filter((_, i) => i !== index));
    }
  };

  const handleMoveQuestionUp = (index: number) => {
    if (index === 0) return;
    const newQuestions = [...questions];
    [newQuestions[index - 1], newQuestions[index]] = [newQuestions[index], newQuestions[index - 1]];
    setQuestions(newQuestions);
  };

  const handleMoveQuestionDown = (index: number) => {
    if (index === questions.length - 1) return;
    const newQuestions = [...questions];
    [newQuestions[index], newQuestions[index + 1]] = [newQuestions[index + 1], newQuestions[index]];
    setQuestions(newQuestions);
  };

  const handleSave = async (saveStatus: "draft" | "published") => {
    if (!title.trim()) {
      alert("Please enter a quiz title");
      return;
    }

    if (saveStatus === "published") {
      if (questions.length === 0) {
        alert("Please add at least one question before publishing");
        return;
      }
      for (let i = 0; i < questions.length; i++) {
        const question = questions[i];
        if (!question?.text?.trim()) {
          alert(`Question ${i + 1} is missing question text`);
          return;
        }
        if (question.type === "fill-in-the-blank") {
          if (!(question.correctAnswers || []).some(a => a?.trim())) {
            alert(`Question ${i + 1} must have at least one accepted answer`);
            return;
          }
        } else {
          if (!question.options?.some((opt) => opt?.isCorrect)) {
            alert(`Question ${i + 1} must have a correct answer selected`);
            return;
          }
          if (question.options.some((opt) => !opt?.text?.trim())) {
            alert(`Question ${i + 1} has empty options`);
            return;
          }
        }
      }
    }

    setIsSaving(true);
    setSaveMessage(null);

    const payload = {
      quiz: {
        title,
        description,
        passing_score: passingScore,
        status: saveStatus,
      },
      questions: questions.map((q) => {
        const base: Record<string, unknown> = {
          question_text: q.text,
          question_type: q.type,
        };
        if (q.type === "fill-in-the-blank") {
          base.correct_answers = (q.correctAnswers || []).filter(a => a?.trim());
        } else {
          base.options = q.options.map((opt) => opt.text);
          base.correct_option_index = q.options.findIndex((opt) => opt.isCorrect);
        }
        if (q.imageSignedId) {
          base.image_signed_id = q.imageSignedId;
        }
        if (q.keepImage === false) {
          base.keep_image = false;
        }
        return base;
      }),
    };

    try {
      if (onSave) {
        await onSave(payload);
      } else if (saveUrl) {
        const response = quizId
          ? await apiPatch(saveUrl, payload)
          : await apiPost(saveUrl, payload);

        if (!response.ok) {
          const data = await response.json().catch(() => null);
          const message = data?.errors?.join(", ") || "Failed to save quiz.";
          alert(message);
          return;
        }
        setStatus(saveStatus);
        const label = saveStatus === "published" ? "published" : "saved as draft";
        setSaveMessage(`Quiz ${label} successfully!`);
        setTimeout(() => setSaveMessage(null), 4000);
      }
    } catch (error) {
      console.error("Error saving quiz:", error);
      alert("Failed to save quiz. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  if (showPreview) {
    return (
      <div className="min-h-screen bg-background">
        <QuizPreview
          title={title || "Untitled Quiz"}
          description={description}
          questions={questions}
          passingScore={passingScore}
          onClose={() => setShowPreview(false)}
        />
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-background ${className}`}>
      <div className="max-w-5xl mx-auto p-8">
        {/* Header */}
        <div className="mb-8">
          <a
            href={backUrl}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            <span className="text-sm">Back to Quiz Library</span>
          </a>
          <div className="flex items-center gap-3 mb-2">
            <h1 className="text-4xl font-bold text-foreground">
              {quizId ? "Edit Quiz" : "Create New Quiz"}
            </h1>
            {quizId && (
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                status === "published"
                  ? "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                  : "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
              }`}>
                {status === "published" ? "Published" : "Draft"}
              </span>
            )}
          </div>
          <p className="text-lg text-muted-foreground">
            {quizId
              ? "Update your quiz details and questions"
              : "Create a new quiz for your learning modules"}
          </p>
        </div>

        {/* Quiz Details */}
        <div className="bg-card border border-border rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">
            Quiz Details
          </h2>

          <div className="space-y-4">
            <div>
              <Label htmlFor="title" className="text-sm font-medium text-foreground mb-2 block">
                Quiz Title *
              </Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g., Customer Discovery Fundamentals"
              />
            </div>

            <div>
              <Label htmlFor="description" className="text-sm font-medium text-foreground mb-2 block">
                Description
              </Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of what this quiz covers..."
                className="min-h-[80px] resize-none"
              />
            </div>

            <div>
              <Label className="text-sm font-medium text-foreground mb-2 block">
                Passing Score: {passingScore}%
              </Label>
              <div className="flex items-center gap-4">
                <Slider
                  value={[passingScore]}
                  onValueChange={(value) => setPassingScore(value[0])}
                  min={60}
                  max={100}
                  step={5}
                  className="flex-1"
                />
                <span className="text-sm text-muted-foreground w-16 text-right">
                  {passingScore}%
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Students must score at least {passingScore}% to pass this quiz
              </p>
            </div>
          </div>
        </div>

        {/* Questions Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                Questions ({questions.length})
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Add and manage quiz questions.
              </p>
            </div>
            <AcademicianButton
              variant="secondary"
              leftIcon={<PlusIcon />}
              onClick={handleAddQuestion}
            >
              Add Question
            </AcademicianButton>
          </div>

          {questions.length > 0 ? (
            <div className="space-y-4">
              {questions.map((question, index) => (
                <QuestionEditor
                  key={question.id}
                  question={question}
                  questionNumber={index + 1}
                  uploadImageUrl={uploadImageUrl}
                  onChange={(updatedQuestion) =>
                    handleUpdateQuestion(index, updatedQuestion)
                  }
                  onDelete={() => handleDeleteQuestion(index)}
                  onMoveUp={() => handleMoveQuestionUp(index)}
                  onMoveDown={() => handleMoveQuestionDown(index)}
                  isFirst={index === 0}
                  isLast={index === questions.length - 1}
                />
              ))}
            </div>
          ) : (
            <div className="bg-card border border-dashed border-border rounded-lg p-12 text-center">
              <div className="max-w-md mx-auto">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <PlusIcon className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  No questions yet
                </h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Get started by adding your first question to the quiz
                </p>
                <AcademicianButton
                  variant="primary"
                  leftIcon={<PlusIcon />}
                  onClick={handleAddQuestion}
                >
                  Add Your First Question
                </AcademicianButton>
              </div>
            </div>
          )}
        </div>

        {/* Success Message */}
        {saveMessage && (
          <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-800 dark:text-green-300 text-sm">
            <CheckCircleIcon className="h-4 w-4 shrink-0" />
            {saveMessage}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-6 border-t border-border">
          <div className="flex gap-3">
            <a href={backUrl}>
              <AcademicianButton variant="ghost">
                Cancel
              </AcademicianButton>
            </a>
            {questions.length > 0 && (
              <AcademicianButton
                variant="secondary"
                leftIcon={<EyeIcon />}
                onClick={() => setShowPreview(true)}
              >
                Preview Quiz
              </AcademicianButton>
            )}
          </div>

          <div className="flex gap-3">
            <AcademicianButton
              variant="secondary"
              leftIcon={<SaveIcon />}
              onClick={() => handleSave("draft")}
              loading={isSaving}
              disabled={isSaving}
            >
              Save as Draft
            </AcademicianButton>
            <AcademicianButton
              variant="primary"
              leftIcon={<SendIcon />}
              onClick={() => handleSave("published")}
              loading={isSaving}
              disabled={isSaving}
            >
              Publish Quiz
            </AcademicianButton>
          </div>
        </div>
      </div>
    </div>
  );
}
